// FNP-1011: the playbook export format, console side.
//
// ⚠️ THE FORMAT IS THE BACKEND'S CONTRACT, NOT THIS APP'S INVENTION.
//
// flexinet-workflows-api's `playbook-export.service.ts` defines the shape and
// `playbook-import.service.ts` enforces it on import. This module produces the
// same bytes so a file exported here imports cleanly there. If the two ever
// disagree, the backend wins — it is the half that validates.
//
// Three properties make an exported file useful rather than merely portable:
//
//  1. NO IDENTITY. No `id` anywhere. Ids are minted per environment; reusing
//     them orphans records that reference the old ones (FNP-914).
//  2. NO ENVIRONMENT BINDING. No `stateMachineArn` — it embeds the AWS account
//     id, so importing dev's ARN into test would point test's playbook at
//     DEV's state machine. On a teardown playbook that is a cross-environment
//     deletion. The ARN is bound at deploy time by PlaybookArnPatcher.
//  3. DIFFABLE. Fixed key order, phases and steps sorted by position, absent
//     optionals omitted rather than emitted as null.
//
// ⚠️ Property 3 is what makes this a VERIFICATION tool rather than a copy tool.
// Without it, two exports of equivalent playbooks differ and dev-vs-test drift
// cannot be diffed — which is exactly the problem FNP-997, FNP-991 and
// FNP-1002 all were.

export const PLAYBOOK_DEFINITION_FORMAT = 'flexinet.playbook';
export const PLAYBOOK_DEFINITION_VERSION = 1;

export interface StepDefinition {
    name: string;
    displayName?: string;
    description?: string;
    position: number;
    taskStateName?: string;
    targetService?: string;
    targetOperation?: string;
    parameters?: string;
}

export interface PhaseDefinition {
    name: string;
    displayName?: string;
    description?: string;
    position: number;
    steps: StepDefinition[];
}

export interface PlaybookDefinition {
    format: string;
    formatVersion: number;
    name: string;
    displayName?: string;
    description?: string;
    category?: string;
    status?: string;
    inputSchema?: string;
    phases: PhaseDefinition[];
}

// What getPlaybookForExport returns.
export interface ExportSourceStep {
    id?: string;
    phaseId?: string;
    name: string;
    displayName?: string | null;
    description?: string | null;
    position: number;
    taskStateName?: string | null;
    targetService?: string | null;
    targetOperation?: string | null;
    parameters?: unknown;
}

export interface ExportSourcePhase {
    id: string;
    name: string;
    displayName?: string | null;
    description?: string | null;
    position: number;
    steps?: ExportSourceStep[] | null;
}

export interface ExportSourcePlaybook {
    id?: string;
    name: string;
    displayName?: string | null;
    description?: string | null;
    category?: string | null;
    status?: string | null;
    inputSchema?: string | null;
    phases?: ExportSourcePhase[] | null;
}

// Absent optionals are omitted rather than emitted as null, so an unset field
// does not add a line to every diff. Empty strings count as absent: a field
// cleared in the console and a field never set describe the same playbook.
const omitEmpty = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
    const out: Record<string, unknown> = {};
    Object.keys(obj).forEach((key) => {
        const value = obj[key];
        if (value !== null && value !== undefined && value !== '') {
            out[key] = value;
        }
    });
    return out as Partial<T>;
};

// ⚠️ AWSJSON IS DOUBLE-WRAPPED ON READ. Verified live 2026-08-16.
//
//   DynamoDB    {"S": "{}"}   <- the truth, 2 characters
//   getPlaybook '"{}"'        <- what this client receives, 4 characters
//
// The AWSJSON serializer JSON-encodes the stored string a second time on the
// way out. Passing it through unchanged writes `"{}"` back on import, and the
// next export produces `"\"{}\""` — every round trip adds a layer, and
// parseStepParameters() then yields a STRING instead of an object, so the
// orchestrator runs with parameters it cannot use. Silently. That is FNP-1000
// one level up.
//
// Peel exactly one layer when the value decodes to a string; leave genuine
// scalars alone.
const exportParameters = (value: unknown): string | undefined => {
    if (value === null || value === undefined) {
        return undefined;
    }

    if (typeof value === 'string') {
        if (value === '') {
            return undefined;
        }
        try {
            const decoded = JSON.parse(value) as unknown;
            if (typeof decoded === 'string') {
                return decoded === '' ? undefined : decoded;
            }
        } catch {
            // Not JSON — leave exactly as found.
        }
        return value;
    }

    return JSON.stringify(value);
};

// position, then name — position alone is not guaranteed unique, and an
// unstable tie-break would make two exports of the same playbook differ.
const byPosition = (a: {position: number; name: string}, b: {position: number; name: string}) =>
    a.position - b.position || a.name.localeCompare(b.name);

export const buildPlaybookDefinition = (source: ExportSourcePlaybook): PlaybookDefinition => {
    const phases = [...(source.phases ?? [])].sort(byPosition).map((phase) => {
        const steps = [...(phase.steps ?? [])].sort(byPosition).map((step) => omitEmpty({
            name: step.name,
            displayName: step.displayName ?? undefined,
            description: step.description ?? undefined,
            position: step.position,
            taskStateName: step.taskStateName ?? undefined,
            targetService: step.targetService ?? undefined,
            targetOperation: step.targetOperation ?? undefined,
            parameters: exportParameters(step.parameters),
        }) as StepDefinition);

        // `steps` attached AFTER omitEmpty so an empty array survives — "a
        // phase with no steps" is a real state, and dropping the key makes it
        // indistinguishable from a malformed phase on import.
        return {
            ...(omitEmpty({
                name: phase.name,
                displayName: phase.displayName ?? undefined,
                description: phase.description ?? undefined,
                position: phase.position,
            }) as PhaseDefinition),
            steps,
        };
    });

    return {
        format: PLAYBOOK_DEFINITION_FORMAT,
        formatVersion: PLAYBOOK_DEFINITION_VERSION,
        ...(omitEmpty({
            name: source.name,
            displayName: source.displayName ?? undefined,
            description: source.description ?? undefined,
            category: source.category ?? undefined,
            status: source.status ?? undefined,
            inputSchema: source.inputSchema ?? undefined,
        }) as {name: string}),
        phases,
    };
};

// 2-space indent so the file is git-diffable and byte-comparable.
export const serializePlaybookDefinition = (definition: PlaybookDefinition): string =>
    `${JSON.stringify(definition, null, 2)}\n`;

export interface DefinitionSummary {
    name: string;
    phaseCount: number;
    stepCount: number;
}

// Parsed for the PREVIEW only — the backend is the authority and re-validates
// everything. This exists so the admin sees what they are about to create
// before anything is written, which is the only moment they can catch that
// they picked the wrong file.
export const summarizeDefinition = (raw: string): DefinitionSummary => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(raw);
    } catch {
        throw new Error(
            'That file is not valid JSON. Choose a playbook definition exported '
            + 'from a Flexi Network console.',
        );
    }

    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('A playbook definition must be a JSON object.');
    }

    const def = parsed as Partial<PlaybookDefinition>;

    if (def.format !== PLAYBOOK_DEFINITION_FORMAT) {
        throw new Error(
            'That file is not a playbook definition. Import expects a file produced '
            + 'by the console\'s playbook export.',
        );
    }

    if (def.formatVersion !== PLAYBOOK_DEFINITION_VERSION) {
        throw new Error(
            `That file uses definition version ${String(def.formatVersion)}; this console `
            + `supports version ${PLAYBOOK_DEFINITION_VERSION}. Re-export the playbook from `
            + 'its source environment.',
        );
    }

    // Refused in the preview as well as the backend: a file carrying an ARN is
    // evidence the exporter is wrong, and the admin should see that before
    // clicking rather than as a server error afterwards.
    if ((def as Record<string, unknown>).stateMachineArn) {
        throw new Error(
            'That definition carries a state machine ARN. ARNs are environment-specific '
            + 'and are bound at deploy time — remove it from the file.',
        );
    }

    if (typeof def.name !== 'string' || def.name.trim() === '') {
        throw new Error('That definition has no playbook name.');
    }

    if (!Array.isArray(def.phases)) {
        throw new Error('That definition has no phases.');
    }

    return {
        name: def.name,
        phaseCount: def.phases.length,
        stepCount: def.phases.reduce(
            (total, phase) => total + (Array.isArray(phase?.steps) ? phase.steps.length : 0),
            0,
        ),
    };
};
